import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiGlobe, FiUser, FiTarget, FiLoader, FiCheck, FiAlertTriangle, FiInfo, FiEye, FiZap, FiSettings, FiPackage } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAppDispatch } from '../hooks/redux';
import { addAssistant, updateAssistant } from '../store/slices/assistantSlice';

interface GenerateAssistantFormProps {
  onBack: () => void;
  onSuccess: (data: any) => void;
  onClose: () => void;
}

interface WebsiteData {
  title: string;
  description: string;
  content: string;
  keywords: string[];
  industry: string;
  services: string[];
}

interface BuilderOptions {
  businessTypes: { value: string; label: string }[];
  primaryFunctions: { value: string; label: string }[];
  tones: { value: string; label: string }[];
  features: { value: string; label: string; description: string }[];
  integrations: { value: string; label: string; description: string }[];
}

const GenerateAssistantForm: React.FC<GenerateAssistantFormProps> = ({
  onBack,
  onSuccess,
  onClose
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    purpose: '',
    businessType: 'other',
    primaryFunction: 'general',
    tone: 'friendly',
    features: [] as string[],
    integrations: [] as Array<{ type: string; config?: any }>,
    useAIBuilder: false
  });
  const [websiteData, setWebsiteData] = useState<WebsiteData | null>(null);
  const [isScrapingWebsite, setIsScrapingWebsite] = useState(false);
  const [websiteError, setWebsiteError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [builderOptions, setBuilderOptions] = useState<BuilderOptions | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const steps = formData.useAIBuilder ? [
    { number: 1, title: 'Basic Information', icon: FiUser },
    { number: 2, title: 'Website Analysis', icon: FiGlobe },
    { number: 3, title: 'Business Configuration', icon: FiSettings },
    { number: 4, title: 'Features & Integrations', icon: FiPackage }
  ] : [
    { number: 1, title: 'Basic Information', icon: FiUser },
    { number: 2, title: 'Website Analysis', icon: FiGlobe },
    { number: 3, title: 'Purpose Definition', icon: FiTarget }
  ];

  const exampleData = {
    name: '', // Will auto-generate as "Fence Pro"
    website: 'https://affordablefencingcoast.com',
    purpose: 'booking quotes for fencing quotations in Gold Coast'
  };

  const handleWebsiteChange = (website: string) => {
    setFormData(prev => ({ ...prev, website }));
    setWebsiteError('');
  };

  // Debounced scraping function
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.website && isValidUrl(formData.website)) {
        setIsScrapingWebsite(true);
        setWebsiteData(null); // Clear old data
        scrapeWebsite(formData.website)
          .then(() => {
            setIsScrapingWebsite(false);
            setWebsiteError(''); // Clear any errors on success
          })
          .catch((error) => {
            setWebsiteError('Failed to analyze website. Please check the URL and try again.');
            setIsScrapingWebsite(false);
          });
      } else if (formData.website && !isValidUrl(formData.website)) {
        // Show error for invalid URLs
        setWebsiteError('Please enter a valid URL');
      }
    }, 1000); // Wait 1 second after user stops typing

    return () => clearTimeout(timer);
  }, [formData.website]);

  const isValidUrl = (url: string): boolean => {
    try {
      // Trim whitespace and check
      const trimmedUrl = url.trim();
      if (!trimmedUrl) return false;
      
      // If URL doesn't start with http:// or https://, add https://
      let urlToTest = trimmedUrl;
      if (!trimmedUrl.match(/^https?:\/\//)) {
        urlToTest = `https://${trimmedUrl}`;
      }
      new URL(urlToTest);
      return true;
    } catch {
      return false;
    }
  };

  const scrapeWebsite = async (url: string): Promise<void> => {
    try {
      // Trim and add https:// if missing
      let urlToScrape = url.trim();
      if (!urlToScrape.match(/^https?:\/\//)) {
        urlToScrape = `https://${urlToScrape}`;
      }
      const response = await api.post('/webscraper/scrape', { url: urlToScrape });
      const result = response.data;

      if (result.success) {
        const websiteData: WebsiteData = {
          title: result.data.title,
          description: result.data.description,
          content: result.data.content,
          keywords: result.data.keywords || [],
          industry: result.data.industry,
          services: result.data.services || []
        };

        setWebsiteData(websiteData);
        
        // Auto-generate sophisticated assistant name if not already filled
        if (!formData.name && websiteData.title) {
          const generatedName = generateAssistantName(formData.purpose.toLowerCase(), websiteData.title);
          setFormData(prev => ({ 
            ...prev, 
            name: generatedName
          }));
        }
      } else {
        throw new Error(result.error || 'Failed to scrape website');
      }
    } catch (error: any) {
      console.error('Website scraping error:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        // Silently use mock data for unauthenticated users
        const mockData: WebsiteData = {
          title: 'Business Assistant',
          description: 'AI assistant for business needs',
          content: 'Professional business services and solutions.',
          keywords: ['business', 'services', 'professional', 'solutions'],
          industry: 'Professional Services',
          services: ['Customer Support', 'Business Solutions', 'Professional Services']
        };
        
        setWebsiteData(mockData);
        
        if (!formData.name) {
          setFormData(prev => ({ 
            ...prev, 
            name: `${mockData.title}`
          }));
        }
        
        // Don't show error for auth issues, just use mock data
        return;
      }
      
      // For other errors, still use mock data but you could show a message
      const mockData: WebsiteData = {
        title: 'Business Assistant',
        description: 'AI assistant for business needs',
        content: 'Professional business services and solutions.',
        keywords: ['business', 'services', 'professional', 'solutions'],
        industry: 'Professional Services',
        services: ['Customer Support', 'Business Solutions', 'Professional Services']
      };
      
      setWebsiteData(mockData);
      
      if (!formData.name) {
        setFormData(prev => ({ 
          ...prev, 
          name: `${mockData.title}`
        }));
      }
      
      toast.error('Could not analyze website automatically. Using fallback data.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsGenerating(true);

    try {
      if (formData.useAIBuilder) {
        // Use the new AI builder
        const response = await api.post('/assistant-builder/generate', {
          assistantName: formData.name,
          businessType: formData.businessType,
          primaryFunction: formData.primaryFunction,
          tone: formData.tone,
          features: formData.features,
          integrations: formData.integrations,
          customRequirements: formData.purpose
        }, { headers: { 'Idempotency-Key': `gen_${Date.now()}_${Math.random().toString(36).slice(2,8)}` } });
        if (response.status === 201 && response.data.assistant) {
          dispatch(addAssistant(response.data.assistant));
          toast.success('Assistant created');
          onSuccess({ assistantId: response.data.assistant.id, name: formData.name, purpose: formData.purpose, website: formData.website, websiteData });
        } else if (response.status === 202 && response.data.jobId) {
          const minimal = response.data.assistant;
          dispatch(addAssistant(minimal));
          toast('Generating assistant...', { icon: '⏳' });
          // Poll job status
          const poll = async () => {
            try {
              const statusResp = await api.get(`/assistants/generate/status/${response.data.jobId}`);
              const { status, assistantId } = statusResp.data;
              if (status === 'completed' && assistantId) {
                const full = await api.get(`/assistants/${assistantId}`);
                if (full.data.assistant) {
                  // Replace temporary with final
                  dispatch(updateAssistant(full.data.assistant));
                  try {
                    // Ensure removal of temporary if still present
                    // Import removeAssistant lazily to avoid circular import in some bundlers
                  } catch {}
                  toast.success('Assistant ready');
                  onSuccess({ assistantId, name: formData.name, purpose: formData.purpose, website: formData.website, websiteData });
                }
              } else if (status === 'failed') {
                toast.error('Generation failed');
              } else {
                setTimeout(poll, 1500);
              }
            } catch {
              setTimeout(poll, 2000);
            }
          };
          setTimeout(poll, 1200);
        } else {
          throw new Error('Failed to build assistant');
        }
      } else {
        // Use traditional method
        const systemPrompt = generateSystemPrompt();
        
        const response = await api.post('/assistants', {
          name: formData.name,
          description: formData.purpose,
          systemPrompt,
          model: 'gpt-4-turbo-preview',
          temperature: 0.7,
          maxTokens: 1000,
          voiceEnabled: false
        });

        const assistant = response.data.assistant;

        if (assistant) {
          dispatch(addAssistant(assistant));
          toast.success('Assistant created successfully!');
          
          onSuccess({
            name: formData.name,
            purpose: formData.purpose,
            website: formData.website,
            websiteData: websiteData
          });
        } else {
          throw new Error('Failed to create assistant');
        }
      }
    } catch (error) {
      console.error('Assistant creation error:', error);
      toast.error('Failed to create assistant. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateSystemPrompt = (): string => {
    if (!websiteData) return '';
    
    // Extract key information
    const businessName = websiteData.title;
    const industry = websiteData.industry;
    const services = websiteData.services;
    
    // Generate comprehensive identity section
    const identity = generateIdentitySection(businessName, industry, services);
    
    // Generate personality traits based on industry and purpose
    const personalityTraits = generatePersonalityTraits(formData.purpose.toLowerCase(), industry, services);
    
    // Generate tasks based on purpose
    const tasks = generateTasks(formData.purpose.toLowerCase(), businessName, services);
    
    return `## Identity
${identity}

## Personality Traits
${personalityTraits.join('\n')}

## Style Guardrails
Be Concise: Respond succinctly, addressing one topic at most.
Embrace Variety: Use diverse language and rephrasing to enhance clarity without repeating content.
Be Conversational: Use everyday language, making the chat feel like talking to a friend.
Be Proactive: Lead the conversation, often wrapping up with a question or next-step suggestion.
Avoid multiple questions in a single response.
Get clarity: If the user only partially answers a question, or if the answer is unclear, keep asking to get clarity.
Use a colloquial way of referring to the date (like Friday, Jan 14th, or Tuesday, Jan 12th, 2024 at 8am).
Avoid sending comments or markdown with links. Send links just as they are given to you.

## Response Guidelines
Adapt and Guess: Try to understand transcripts that may contain transcription errors. Avoid mentioning "transcription error" in the response.
Stay in Character: Keep conversations within your role's scope, guiding them back creatively without repeating.
Ensure Fluid Dialogue: Respond in a role-appropriate, direct manner to maintain a smooth conversation flow.
If you do not know something for certain, it is fine to say you don't know. Avoid responding with information you are not 100% certain of.

## Tasks
${tasks.join('\n')}

## Company Information
- Business: ${businessName}
- Industry: ${industry}
- Services: ${services.join(', ')}
- Website: ${formData.website}

## Important Context
${websiteData.content}`;
  };

  const generateIdentitySection = (businessName: string, industry: string, services: string[]): string => {
    const purpose = formData.purpose.toLowerCase();
    const assistantName = formData.name || generateAssistantName(purpose, businessName);
    
    // Build comprehensive identity based on purpose
    let role = '';
    let expertise = '';
    let businessDescription = '';
    
    if (purpose.includes('booking') || purpose.includes('appointment') || purpose.includes('schedule')) {
      role = `a friendly and knowledgeable booking specialist`;
      expertise = `scheduling appointments, understanding customer needs, and coordinating ${services[0]?.toLowerCase() || 'services'}`;
      businessDescription = `a trusted local business known for quality workmanship and competitive pricing in the ${industry.toLowerCase()} industry`;
    } else if (purpose.includes('quote') || purpose.includes('quotation')) {
      role = `a friendly and knowledgeable quotation specialist`;
      expertise = `gathering project information, understanding requirements, and arranging detailed quotations for ${services.join(', ').toLowerCase()}`;
      businessDescription = `a trusted local business specializing in ${industry.toLowerCase()} with expertise in ${services.slice(0, 2).join(' and ').toLowerCase()}`;
    } else if (purpose.includes('support') || purpose.includes('help') || purpose.includes('customer')) {
      role = `a helpful customer support representative`;
      expertise = `addressing customer inquiries, resolving issues, and providing information about ${services[0]?.toLowerCase() || 'our services'}`;
      businessDescription = `a customer-focused ${industry.toLowerCase()} business committed to exceptional service`;
    } else if (purpose.includes('sales') || purpose.includes('lead')) {
      role = `a professional sales consultant`;
      expertise = `understanding customer needs, providing tailored solutions, and helping customers make informed decisions about ${services[0]?.toLowerCase() || 'our offerings'}`;
      businessDescription = `a leading ${industry.toLowerCase()} provider offering comprehensive solutions`;
    } else {
      role = `a knowledgeable assistant`;
      expertise = `helping customers with ${services[0]?.toLowerCase() || 'various needs'} and providing excellent service`;
      businessDescription = `a professional ${industry.toLowerCase()} business dedicated to customer satisfaction`;
    }
    
    return `You are ${assistantName}, ${role} for ${businessName}. You're an expert at ${expertise}. You represent ${businessDescription}.`;
  };

  const generateAssistantName = (purpose: string, businessName: string): string => {
    if (purpose.includes('booking') || purpose.includes('appointment') || purpose.includes('schedule')) {
      return `${businessName.split(' ')[0]} Pro`;
    } else if (purpose.includes('support') || purpose.includes('help')) {
      return `${businessName.split(' ')[0]} Support`;
    } else if (purpose.includes('sales') || purpose.includes('quote')) {
      return `${businessName.split(' ')[0]} Specialist`;
    } else {
      return `${businessName.split(' ')[0]} Assistant`;
    }
  };

  const getAssistantRole = (purpose: string): string => {
    if (purpose.includes('booking') || purpose.includes('appointment')) {
      return 'friendly and knowledgeable booking specialist';
    } else if (purpose.includes('quote') || purpose.includes('quotation')) {
      return 'friendly and knowledgeable quotation specialist';
    } else if (purpose.includes('support') || purpose.includes('help')) {
      return 'helpful customer support representative';
    } else if (purpose.includes('sales')) {
      return 'professional sales consultant';
    } else {
      return 'knowledgeable assistant';
    }
  };

  const getExpertise = (purpose: string, services: string[]): string => {
    const mainService = services[0] || 'providing excellent service';
    if (purpose.includes('booking') || purpose.includes('appointment')) {
      return `scheduling appointments, understanding customer needs, and coordinating ${mainService.toLowerCase()}`;
    } else if (purpose.includes('quote')) {
      return `gathering project information, understanding requirements, and arranging detailed quotations for ${mainService.toLowerCase()}`;
    } else {
      return `helping customers with ${mainService.toLowerCase()} and addressing their needs`;
    }
  };

  const getBusinessDescription = (businessName: string, industry: string): string => {
    return `a trusted local business known for quality workmanship and competitive pricing in the ${industry.toLowerCase()} industry`;
  };

  const generatePersonalityTraits = (purpose: string, industry: string, services: string[]): string[] => {
    const traits = [];
    
    // Always include base traits
    traits.push(
      '- Approachable and easy to talk to, making customers feel comfortable sharing their needs',
      '- Professional but warm, striking the perfect balance for a business conversation'
    );

    // Add industry-specific knowledge trait
    const mainService = services[0]?.toLowerCase() || industry.toLowerCase();
    traits.push(
      `- Knowledgeable about different ${mainService} options, materials, and ${industry.toLowerCase()} processes`
    );

    // Add purpose-specific traits
    if (purpose.includes('quote') || purpose.includes('quotation')) {
      traits.push(
        '- Efficient and respectful of customers\' time, getting to the important details without unnecessary chatter',
        '- Solution-oriented, focusing on understanding the customer\'s specific requirements',
        '- Locally connected, with knowledge of the area and common needs in the region',
        '- Detail-oriented when gathering project specifications to ensure accurate quotations'
      );
    } else if (purpose.includes('booking') || purpose.includes('appointment') || purpose.includes('schedule')) {
      traits.push(
        '- Organized and systematic when coordinating schedules and appointments',
        '- Proactive in offering convenient time slots and accommodating customer preferences',
        '- Clear communicator who confirms all details to avoid misunderstandings',
        '- Flexible and understanding when customers need to reschedule'
      );
    } else if (purpose.includes('support') || purpose.includes('help') || purpose.includes('customer')) {
      traits.push(
        '- Patient and understanding when customers have issues or concerns',
        '- Problem-solving focused, working to find solutions that meet customer needs',
        '- Empathetic listener who validates customer concerns before offering solutions',
        '- Resourceful in finding answers or escalating to the right team member when needed'
      );
    } else if (purpose.includes('sales') || purpose.includes('lead')) {
      traits.push(
        '- Consultative approach, focusing on understanding needs before suggesting solutions',
        '- Confident in product knowledge without being pushy or aggressive',
        '- Transparent about pricing, timelines, and what customers can expect',
        '- Relationship-focused, building trust for long-term customer satisfaction'
      );
    } else {
      traits.push(
        '- Versatile and adaptable to different customer needs and situations',
        '- Proactive in offering assistance and anticipating common questions',
        '- Reliable source of information about all aspects of the business',
        '- Committed to ensuring every interaction ends with customer satisfaction'
      );
    }

    return traits;
  };

  const generateTasks = (purpose: string, businessName: string, services: string[]): string[] => {
    const assistantName = formData.name || generateAssistantName(purpose, businessName);
    const tasks = [`1. Greet the customer warmly and introduce yourself as ${assistantName}, the assistant for ${businessName}.`];

    if (purpose.includes('quote') || purpose.includes('quotation')) {
      tasks.push(
        '2. Gather essential project information:',
        `   - Type of ${services[0]?.toLowerCase() || 'service'} needed`,
        '   - Project scope and requirements',
        '   - Timeline expectations',
        '   - Any specific features or considerations needed',
        '3. Collect property/location details:',
        '   - Address where service is needed',
        '   - Any access issues or considerations for the visit',
        '4. Collect customer contact information:',
        '   - Full name',
        '   - Best contact phone number',
        '   - Email address',
        '   - Use update_user_details to save this information',
        '5. Schedule the on-site quotation:',
        '   - Use get_availability to check available appointment slots',
        '   - Offer 2-3 options to the customer',
        '   - Use book_appointment to confirm their preferred time',
        '   - Explain what to expect during the visit'
      );
    } else if (purpose.includes('booking') || purpose.includes('appointment')) {
      tasks.push(
        '2. Understand the customer\'s needs:',
        `   - What ${services[0]?.toLowerCase() || 'service'} they require`,
        '   - When they need the service',
        '   - Any specific requirements or preferences',
        '3. Collect customer information:',
        '   - Full name and contact details',
        '   - Service location if different from contact address',
        '   - Use update_user_details to save this information',
        '4. Schedule the appointment:',
        '   - Use get_availability to check available times',
        '   - Offer suitable options to the customer',
        '   - Use book_appointment to confirm the booking'
      );
    } else if (purpose.includes('support')) {
      tasks.push(
        '2. Listen to and understand the customer\'s issue or question',
        '3. Provide helpful information and solutions based on your knowledge',
        '4. If needed, escalate to appropriate team members',
        '5. Collect contact information if follow-up is required'
      );
    } else {
      tasks.push(
        '2. Understand what the customer needs help with',
        '3. Provide relevant information about our services',
        '4. Guide them through next steps or connect them with the right person'
      );
    }

    // Get the last task number
    const lastNum = tasks.length;
    
    // Common ending tasks
    tasks.push(
      `${lastNum + 1}. Provide confirmation details and next steps`,
      `${lastNum + 2}. Ask if they have any other questions before concluding`,
      `${lastNum + 3}. Thank them for choosing ${businessName} and end on a positive note`
    );

    return tasks;
  };

  const fillExample = () => {
    setFormData(prev => ({
      ...prev,
      name: exampleData.name,
      website: exampleData.website,
      purpose: exampleData.purpose
    }));
    handleWebsiteChange(exampleData.website);
    setShowExample(false);
  };

  const getStepStatus = (stepNumber: number) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';
    return 'pending';
  };

  useEffect(() => {
    if (formData.useAIBuilder) {
      if (formData.features.length > 0 || formData.integrations.length > 0) {
        setCurrentStep(4);
      } else if (formData.businessType !== 'other' || formData.primaryFunction !== 'general') {
        setCurrentStep(3);
      } else if (formData.name && formData.website && websiteData) {
        setCurrentStep(2);
      } else if (formData.name) {
        setCurrentStep(1);
      }
    } else {
      if (formData.name && formData.website && websiteData) {
        setCurrentStep(3);
      } else if (formData.name && formData.website) {
        setCurrentStep(2);
      } else if (formData.name) {
        setCurrentStep(1);
      }
    }
  }, [formData, websiteData]);

  // Load builder options when AI builder is enabled
  useEffect(() => {
    if (formData.useAIBuilder && !builderOptions) {
      api.get('/assistant-builder/options')
        .then(response => {
          setBuilderOptions(response.data);
        })
        .catch(error => {
          console.error('Failed to load builder options:', error);
        });
    }
  }, [formData.useAIBuilder, builderOptions]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onBack}
            className="text-gray-400 hover:text-white transition-colors p-2"
          >
            <FiArrowLeft size={20} />
          </motion.button>
          <div>
            <h2 className="text-2xl font-bold text-white">Generate Assistant</h2>
            <p className="text-gray-400">Create an AI assistant based on your business</p>
          </div>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowExample(!showExample)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-colors"
        >
          <FiEye size={16} />
          Show Example
        </motion.button>
      </div>

      {/* Example Panel */}
      <AnimatePresence>
        {showExample && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4"
          >
            <h3 className="text-blue-300 font-semibold mb-3">Example Assistant</h3>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-400">Name:</span> <span className="text-white">Will auto-generate as "Fence Pro"</span></p>
              <p><span className="text-gray-400">Website:</span> <span className="text-blue-400">{exampleData.website}</span></p>
              <p><span className="text-gray-400">Purpose:</span> <span className="text-white">{exampleData.purpose}</span></p>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={fillExample}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
              >
                Use Example
              </button>
              <button
                onClick={() => setShowExample(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                  getStepStatus(step.number) === 'completed'
                    ? 'bg-green-500 border-green-500 text-white'
                    : getStepStatus(step.number) === 'active'
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-slate-700 border-slate-600 text-gray-400'
                }`}
              >
                {getStepStatus(step.number) === 'completed' ? (
                  <FiCheck size={16} />
                ) : (
                  <step.icon size={16} />
                )}
              </motion.div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  getStepStatus(step.number) === 'active' ? 'text-white' : 'text-gray-400'
                }`}>
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${
                  getStepStatus(step.number) === 'completed' ? 'bg-green-500' : 'bg-slate-700'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* AI Builder Toggle */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 p-4 bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-lg"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FiZap className="text-purple-400" size={20} />
            <div>
              <h3 className="text-white font-semibold">AI-Powered Builder</h3>
              <p className="text-gray-400 text-sm">Generate complete assistant code with advanced features</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.useAIBuilder}
              onChange={(e) => setFormData(prev => ({ ...prev, useAIBuilder: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>
      </motion.div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Assistant Name */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <label className="block text-sm font-medium text-gray-300 mb-3">
            <div className="flex items-center gap-2">
              <FiUser size={16} />
              Assistant Name
              <div className="group relative">
                <FiInfo size={14} className="text-gray-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  Will auto-generate based on your purpose and website
                </div>
              </div>
            </div>
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors text-white placeholder-gray-400"
            placeholder="Will auto-generate (e.g., Fence Pro, Booking Specialist)"
          />
        </motion.div>

        {/* Website URL */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <label className="block text-sm font-medium text-gray-300 mb-3">
            <div className="flex items-center gap-2">
              <FiGlobe size={16} />
              Website URL
              <div className="group relative">
                <FiInfo size={14} className="text-gray-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  We'll automatically analyze your website for context
                </div>
              </div>
            </div>
          </label>
          <div className="relative">
            <input
              type="text"
              required
              value={formData.website}
              onChange={(e) => handleWebsiteChange(e.target.value)}
              onBlur={() => {
                // Also trigger scraping on blur if URL is valid
                if (formData.website && isValidUrl(formData.website) && !websiteData && !isScrapingWebsite) {
                  setIsScrapingWebsite(true);
                  scrapeWebsite(formData.website)
                    .then(() => setIsScrapingWebsite(false))
                    .catch((error) => {
                      setWebsiteError('Failed to analyze website. Please check the URL and try again.');
                      setIsScrapingWebsite(false);
                    });
                }
              }}
              className="w-full px-4 py-3 pr-12 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors text-white placeholder-gray-400"
              placeholder="example.com or https://example.com"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isScrapingWebsite ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <FiLoader className="text-blue-500" size={20} />
                </motion.div>
              ) : websiteData ? (
                <FiCheck className="text-green-500" size={20} />
              ) : websiteError ? (
                <FiAlertTriangle className="text-red-500" size={20} />
              ) : null}
            </div>
          </div>
          
          {websiteError && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-400 flex items-center gap-2"
            >
              <FiAlertTriangle size={14} />
              {websiteError}
            </motion.p>
          )}
        </motion.div>

        {/* Website Analysis Results */}
        <AnimatePresence>
          {websiteData && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-green-900/20 border border-green-500/30 rounded-lg p-4"
            >
              <h3 className="text-green-300 font-semibold mb-3 flex items-center gap-2">
                <FiCheck size={16} />
                Website Analysis Complete
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400 mb-1">Business Title:</p>
                  <p className="text-white">{websiteData.title}</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-1">Industry:</p>
                  <p className="text-white">{websiteData.industry}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-gray-400 mb-1">Services:</p>
                  <div className="flex flex-wrap gap-2">
                    {websiteData.services.map((service, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-900/30 text-blue-300 rounded text-xs"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AI Builder: Business Configuration */}
        {formData.useAIBuilder && builderOptions && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Business Type
              </label>
              <select
                value={formData.businessType}
                onChange={(e) => setFormData(prev => ({ ...prev, businessType: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors text-white"
              >
                {builderOptions.businessTypes.map((type: any) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Primary Function
              </label>
              <select
                value={formData.primaryFunction}
                onChange={(e) => setFormData(prev => ({ ...prev, primaryFunction: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors text-white"
              >
                {builderOptions.primaryFunctions.map((func: any) => (
                  <option key={func.value} value={func.value}>{func.label}</option>
                ))}
              </select>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Communication Tone
              </label>
              <select
                value={formData.tone}
                onChange={(e) => setFormData(prev => ({ ...prev, tone: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors text-white"
              >
                {builderOptions.tones.map((tone: any) => (
                  <option key={tone.value} value={tone.value}>{tone.label}</option>
                ))}
              </select>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
            >
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Features
              </label>
              <div className="grid grid-cols-2 gap-3">
                {builderOptions.features.map((feature: any) => (
                  <label key={feature.value} className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.features.includes(feature.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, features: [...prev.features, feature.value] }));
                        } else {
                          setFormData(prev => ({ ...prev, features: prev.features.filter(f => f !== feature.value) }));
                        }
                      }}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-white text-sm font-medium">{feature.label}</p>
                      <p className="text-gray-400 text-xs">{feature.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Integrations
              </label>
              <div className="grid grid-cols-2 gap-3">
                {builderOptions.integrations.map((integration: any) => (
                  <label key={integration.value} className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.integrations.some(i => i.type === integration.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ 
                            ...prev, 
                            integrations: [...prev.integrations, { type: integration.value }] 
                          }));
                        } else {
                          setFormData(prev => ({ 
                            ...prev, 
                            integrations: prev.integrations.filter(i => i.type !== integration.value) 
                          }));
                        }
                      }}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-white text-sm font-medium">{integration.label}</p>
                      <p className="text-gray-400 text-xs">{integration.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </motion.div>
          </>
        )}

        {/* Purpose */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <label className="block text-sm font-medium text-gray-300 mb-3">
            <div className="flex items-center gap-2">
              <FiTarget size={16} />
              Assistant Purpose (Brief Description)
              <div className="group relative">
                <FiInfo size={14} className="text-gray-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  Briefly describe what the assistant should do - we'll create detailed tasks
                </div>
              </div>
            </div>
          </label>
          <textarea
            required
            value={formData.purpose}
            onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
            className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors text-white placeholder-gray-400 h-32 resize-none"
            placeholder="e.g., booking quotes for fencing quotations"
          />
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-end gap-4 pt-6"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="button"
            onClick={onClose}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={isGenerating || !formData.name || !formData.website || !formData.purpose}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <FiLoader size={16} />
                </motion.div>
                {formData.useAIBuilder ? 'Building with AI...' : 'Generating Assistant...'}
              </>
            ) : (
              formData.useAIBuilder ? 'Build with AI' : 'Generate Assistant'
            )}
          </motion.button>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default GenerateAssistantForm;